<template lang="html">
  <report-builder
    v-model="reportFilters"
    title="Event Report"
    :sub-title="subTitle"
    type="event"
    command="diningReservationDetailReport"
    saved-report-command="diningSavedReport"
    :command-filter="commandFilter"
    :resources="reportFilterResourceCommands"
    :defaults="defaultReportFilters"
    :headers="headers"
    :report-fields="includeFields"
    :report-filters="currentFilterSelection"
    :report-order="sortOrder"
    :report-grouping="groupOrder"
    :report-grouping-fields="getGroupFields"
    :before-run="beforeRun"
    :grouped="isGrouped"
    show-summary
    summary-caption="Summary"
  >
    <!-- REPORT FORM -->
    <!--  DATE  -->
    <template v-slot:filter-date>
      <v-autocomplete
        v-if="hasSelectedEvents"
        v-model="reportFilters.dates"
        label="Date"
        prepend-icon="mdi-calendar"
        :items="groupedEventsDateList"
        multiple
        chips
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.text }}
          </v-chip>
        </template>
      </v-autocomplete>
      <date-picker
        v-else
        v-model="reportFilters.date"
        label="Date"
        clearable
      />
    </template>

    <!--  EVENT FILTER  -->
    <template v-slot:filter-event>
      <v-autocomplete
        v-model="reportFilters.eventNames"
        prepend-icon="mdi-calendar"
        label="Event"
        :items="groupedSelectableEvents"
        multiple
        item-text="name"
        item-value="name"
        chips
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>
    </template>

    <!--  LOCATION FILTER -->
    <template v-slot:filter-location>
      <v-autocomplete
        v-model="reportFilters.locationIds"
        prepend-icon="mdi-map-marker"
        label="Location"
        :items="filteredLocations"
        multiple
        item-text="name"
        item-value="id"
        chips
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>
    </template>

    <!--  REPORT COLUMNS  -->
    <template v-slot:filter-columns-lg>
      <v-autocomplete
        v-model="reportFilters.columns"
        prepend-icon="mdi-view-list"
        label="Columns"
        :items="columnOptions.filter(c => c.optional)"
        multiple
        item-text="name"
        item-value="id"
        chips
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>
    </template>

    <!--  REPORT DETAIL DISPLAY  -->
    <template v-slot:filter-display>
      <v-autocomplete
        v-model="reportFilters.detailGrouping"
        prepend-icon="mdi-account-group"
        label="Reservation Display"
        :items="detailGroupingOptions"
        item-text="name"
        item-value="id"
      />
    </template>

    <!--  REPORT GROUPING -->
    <template v-slot:filter-grouping>
      <v-autocomplete
        v-model="reportFilters.grouping"
        prepend-icon="mdi-sort"
        label="Grouping"
        :items="groupingOptions"
        item-text="name"
        item-value="id"
        clearable
      />
    </template>

    <!--  REPORT SORT ORDER -->
    <template v-slot:filter-order>
      <v-autocomplete
        v-model="reportFilters.sortBy"
        prepend-icon="mdi-sort"
        label="Sort Order"
        :items="sortOptions"
        item-text="name"
        item-value="id"
      />
    </template>

    <!--  REPORT BLANK LINES AFTER GROUP -->
    <template v-slot:filter-group-lines>
      <v-autocomplete
        v-if="reportFilters.grouping"
        v-model="reportFilters.emptyRows"
        prepend-icon="mdi-table-row-plus-after"
        label="Add empty rows per group"
        :items="emptyRowOptions"
      />
    </template>

    <!-- INCLUDE WAITLISTED -->
    <template v-slot:filter-waitlisted>
      <v-autocomplete
        v-model="reportFilters.includeWaitListMode"
        prepend-icon="mdi-pause-octagon-outline"
        label="Include Waitlisted"
        :items="waitlistModes"
      />
    </template>

    <!-- REPORT HEADERS -->
    <!-- (No header slot overrides needed) -->

    <!-- REPORT ROWS/COLUMNS -->
    <template
      slot="td-names"
      slot-scope="props"
    >
      <span
        v-for="(name, i) in props.header.displayCell(props.row, props.index, props.rows)"
        :key="i"
        class="ft-report-reservee"
      >
        <span
          v-if="name.guestType && !isNonDiscreteRoot(props.row)"
          :class="{'ft-report-reservee-gt': true, 'ft-has-name': !!name.name}"
        >{{ name.guestType }}</span>
        <span
          v-if="name.name"
          class="ft-report-reservee-name"
        >{{ name.name }}</span>
        <span
          v-if="name.count > 1"
          class="ft-report-reservee-count"
        >{{ name.count }}</span>
      </span>
    </template>
    <template
      slot="aggregate-eventCostName"
      slot-scope="props"
    >
      <div>
        <span
          v-for="(costAggregate, i) in props.aggregate"
          :key="i"
          class="ft-itb"
        >
          <span class="ft-itb-text">{{ costAggregate.name }}</span><span class="ft-itb-sep">:&nbsp;</span><span class="ft-itb-value">{{ costAggregate.total }}</span>
          <span
            v-if="(i + 1) < props.aggregate.length"
            class="ft-itb-sep"
          >,&nbsp;</span>
        </span>
      </div>
    </template>
    <template
      slot="aggregate-explodeAnswers"
      slot-scope="props"
    >
      <div>
        <span
          v-for="(answerAggregate, i) in props.aggregate.filter(aa => aa.fromList)"
          :key="i"
          class="ft-itb"
        >
          <span class="ft-itb-text">{{ answerAggregate.value }}</span><span class="ft-itb-sep">:&nbsp;</span><span class="ft-itb-value">{{ answerAggregate.total }}</span>
          <span
            v-if="(i + 1) < props.aggregate.length"
            class="ft-itb-sep"
          >,&nbsp;</span>
        </span>
      </div>
    </template>
    <template
      slot="aggregate-explodeAggregate"
      slot-scope="props"
    >
      <div>
        <span
          v-for="(costAggregate, i) in props.aggregate"
          :key="i"
          class="ft-itb"
        >
          <span class="ft-itb-text">{{ costAggregate.name }}</span><span class="ft-itb-sep">:&nbsp;</span><span class="ft-itb-value">{{ costAggregate.total }}</span>
          <span
            v-if="(i + 1) < props.aggregate.length"
            class="ft-itb-sep"
          >,&nbsp;</span>
        </span>
      </div>
    </template>

    <!-- REPORT SUMMARY -->
    <template slot="summary-body">
      <div>Total Reservees: {{ reserveeCount }}</div>
      <div v-if="noShowCount">
        No Shows: {{ noShowCount }}
      </div>
      <div v-if="waitlistedReservees">
        Waitlisted Reservees: {{ waitlistedReservees }}
      </div>
      <div>Total Reservations: {{ reservationCount }}</div>
      <div v-if="waitlistedReservations">
        Waitlisted Reservations: {{ waitlistedReservations }}
      </div>
      <div v-if="eventCostCounts[0]">
        Event Price Breakdown
      </div>
      <div
        v-for="(eventCost, ei) in sortedEventCountCosts"
        :key="ei"
        class="ft-report-summary-event-cost"
      >
        {{ eventCost.name }}: {{ eventCost.total }}
      </div>
    </template>
  </report-builder>
</template>

<script>
import {helpers, rules, groupableReport} from '@/ft-vue-core'
import detailCommon from '../common/mixins/detailCommon'
import options from './mixins/options'

const {deepClone, verboseDate} = helpers

const INCLUDE_WAITLISTED = 0
const EXCLUDE_WAITLISTED = 1
const ONLY_WAITLISTED = 2

const defaultReportFilters = {
  scale: 1,
  eventNames: [],
  locationIds: [],
  sortBy: 1,
  grouping: null,
  detailGrouping: 2,
  columns: [1, 2, 3, 29],
  date: null,
  dates: [],
  paperSize: null, // Defaults to LETTER
  orientation: null, // Defaults to PORTRAIT
  email: null,
  emptyRows: 0,
  includeWaitListMode: INCLUDE_WAITLISTED
}

export default {
  name: 'EventsReport',
  mixins: [rules, groupableReport, detailCommon, options],
  data: function () {
    return {
      defaultReportFilters: deepClone(defaultReportFilters),
      reportFilters: deepClone(defaultReportFilters),
      eventCostCounts: [],
      waitlistModes: [
        {text: 'Yes', value: INCLUDE_WAITLISTED},
        {text: 'No', value: EXCLUDE_WAITLISTED},
        {text: 'Only', value: ONLY_WAITLISTED}
      ]
    }
  },
  computed: {
    sortedEventCountCosts () {
      // Sort by cost name, pushing 'None' to the bottom
      return this.eventCostCounts.slice().sort((a, b) => a.id ? a.name.toLowerCase().localeCompare(b.name.toLowerCase()) : 1)
    },
    subTitle () {
      const selectedEventNamesDatesText = this.selectedEventNamesDatesText
      return this.selectedDetailGrouping.subTitle + (selectedEventNamesDatesText ? ' - ' + selectedEventNamesDatesText : this.reportFilters.date ? ' - ' + verboseDate(this.reportFilters.date) : '')
    },
    selectedEventNamesDatesText () {
      const eventNames = this.reportFilters.eventNames || []
      const dates = this.reportFilters.dates || []
      return eventNames.join(', ') + (dates[0] ? ' - ' + dates.join(', ') : '')
    },
    eventCostMap () {
      return this.eventCosts.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    detailGroupingMap () {
      return this.detailGroupingOptions.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    selectedDetailGrouping () {
      return this.detailGroupingMap[this.reportFilters.detailGrouping] || {}
    },
    /* overrides super */
    selectedGroupingFilters () {
      return this.selectedGrouping.concat(this.selectedDetailGrouping.group || [])
    },
    groupedByReservation () {
      return this.selectedDetailGrouping.group && this.selectedDetailGrouping.group[0] || this.selectedDetailGrouping.consolidated === true
    }
  },
  /**
   * TODO: Move any methods that don't require access to "this" to a helpers include
   *       There is overhead in calling this.xxx methods
   */
  methods: {
    beforeRun (f) {
      this.subGroupHighlight = true
    },
    getExtraIncludeFields () {
      // Add any fields needed by the selected detail grouping option
      return this.getGroupFields(this.selectedDetailGrouping)
        .concat(['eventCostId', 'eventCostName'])
    },
    reservationChanged (row, rowIndex, rows) {
      return !this.groupedByReservation || (rowIndex === 0 || row.reservationId !== rows[rowIndex - 1].reservationId)
    },
    skipRow (row, rowIndex) {
      // Skip any non-discreteCovers.  (probably should move this to the query filter)
      // return !row.discreteCovers && row.pos > 0 && !row.memberId
      return this.selectedDetailGrouping.consolidated === true && row.pos > 0 || !row.discreteCovers && row.pos > 0 && !row.memberId
    },
    extraSortOrder (f) {
      // Groupable mixin sorts by grouped order first, then we add any other sorting
      return (this.sortOptionMap[f.sortBy] || {}).order || []
    },
    currentFilterSelection (f) {
      var filters = []
      var reservationFilters = []
      // DATE
      if (this.hasSelectedEvents) {
        const dateFilters = this.getSelectedDatesFilter(f)
        if (dateFilters.length) reservationFilters.push({filter: dateFilters})
      } else if (f.date) {
        reservationFilters.push({
          field: 'dateTime',
          operator: 'BETWEEN',
          values: [
            f.date + ' 00:00:00',
            f.date + ' 23:59:59'
          ]
        })
      }
      // LOCATIONS
      if (f.locationIds && f.locationIds.length) {
        reservationFilters.push({field: 'locationId', operator: 'IN', values: f.locationIds})
      }
      // EVENTS
      const eventIds = this.getSelectedEventIds(f)
      if (eventIds.length) {
        // Get specific events
        reservationFilters.push({field: 'eventId', operator: 'IN', values: eventIds})
      } else {
        // Get all event reservations
        reservationFilters.push({field: 'eventId', operator: 'IS_NOT_NULL'})
      }
      // RESERVATION SUB FILTER
      if (reservationFilters.length) {
        filters.push({
          field: 'reservationId',
          operator: 'IN',
          subQueryTarget: 'diningReservation.id',
          subQueryFilter: reservationFilters
        })
      }
      // WAITLIST MODE
      if (f.includeWaitListMode > 0) {
        filters.push({field: 'waitlist', operator: 'EQUAL', values: [f.includeWaitListMode === ONLY_WAITLISTED]})
      }
      // EXCLUDE CANCELLED
      filters.push({field: 'cancelled', operator: 'IS_NULL'})
      console.log('Selected Filters', filters)
      return filters
    },
    validFilters () {
      return true
    },
    reportFilterResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations},
        {command: 'diningEvent', load: this.events, params: {include_fields: ['locations'], order_by: ['name:ASC', 'date:ASC']}},
        {command: 'diningEventCost', load: this.eventCosts}
      ]
    }
  }
}
</script>

<style>
  td .ft-report-reservee {
    white-space: nowrap;
    display: inline-block;
  }
  td .ft-report-reservee .ft-report-reservee-gt.ft-has-name::after {
    content: ": ";
  }
  td .ft-report-reservee .ft-report-reservee-count::before {
    content: " (";
  }
  td .ft-report-reservee .ft-report-reservee-count::after {
    content: ")";
  }
  td .ft-report-reservee:nth-last-child(n+2)::after {
    content: "; ";
  }
  .ft-report-summary-event-cost {
    margin-left: 10px
  }
</style>
